import React from "react";
import { X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

export default function FilterModal({ isOpen, onClose, filters, setFilters }) {
  const handleClear = () => {
    setFilters({
      sortBy: "New",
      order: "Ascending",
      date: "",
      status: "All Status",
    });
  };

  const handleFilter = () => {
    // You can add filter logic here if needed
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-gray-500/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-black text-white w-full max-w-md rounded-xl p-8 pb-10 relative shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              onClick={onClose}
              className="absolute top-6 left-6 text-white hover:opacity-70"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            >
              <X size={24} />
            </motion.button>

            <div className="space-y-5 mt-10">
              {/* Sort By Row */}
              <motion.div
                className="flex justify-between items-center gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label className="font-bold text-sm">Sort by</label>
                <div className="relative flex-1">
                  <select
                    value={filters.sortBy}
                    onChange={(e) =>
                      setFilters({ ...filters, sortBy: e.target.value })
                    }
                    className="w-full bg-white text-black rounded-2xl h-10 px-4 appearance-none outline-none text-xs font-bold"
                  >
                    <option>New</option>
                    <option>Old</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-2.5 text-black pointer-events-none"
                    size={16}
                  />
                </div>
              </motion.div>

              {/* Order Row */}
              <motion.div
                className="flex justify-between items-center gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <label className="font-bold text-sm">Order</label>
                <div className="relative flex-1">
                  <select
                    value={filters.order}
                    onChange={(e) =>
                      setFilters({ ...filters, order: e.target.value })
                    }
                    className="w-full bg-white text-black rounded-2xl h-10 px-4 appearance-none outline-none text-xs font-bold"
                  >
                    <option>Ascending</option>
                    <option>Descending</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-2.5 text-black pointer-events-none"
                    size={16}
                  />
                </div>
              </motion.div>

              <motion.h3
                className="font-bold text-lg pt-2 mb-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Filter by
              </motion.h3>

              {/* Date Input */}
              <motion.div
                className="flex justify-between items-center gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label className="font-bold text-sm">Date</label>
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={filters.date}
                    onChange={(e) =>
                      setFilters({ ...filters, date: e.target.value })
                    }
                    className="w-full bg-white text-black rounded-2xl h-10 px-4 outline-none text-xs font-bold"
                  />
                </div>
              </motion.div>

              {/* Status Row */}
              <motion.div
                className="flex justify-between items-center gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <label className="font-bold text-sm">Status</label>
                <div className="relative flex-1">
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                    className="w-full bg-white text-black rounded-2xl h-10 px-4 appearance-none outline-none text-xs font-bold"
                  >
                    <option>All Status</option>
                    <option>Pending</option>
                    <option>Completed</option>
                    <option>Cancelled</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-2.5 text-black pointer-events-none"
                    size={16}
                  />
                </div>
              </motion.div>

              {/* Footer Actions */}
              <motion.div
                className="flex justify-between items-center pt-6 px-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <motion.button
                  onClick={handleClear}
                  className="font-bold text-sm text-gray-300 hover:text-white"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear
                </motion.button>
                <motion.button
                  onClick={handleFilter}
                  className="flex items-center gap-2 font-bold text-sm text-gray-300 hover:text-white"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Filter <span className="text-xl">→</span>
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}